import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  TextField
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { getProfessionDisplayName } from "@bonliva-crm/common/data/professions";
import { ProfessionsByCountry } from "@graphql";
import { useCostCenters } from "@hooks";
import { getOptions } from "@common/CostCenterSelect";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    costCenterId: string;
    profession: string | null;
    minRequiredTG: number;
  }) => void;
  initialValues?: {
    costCenterId?: string;
    profession?: string | null;
    minRequiredTG?: number;
  };
  isEdit?: boolean;
}

export function TgRequirementEditDialog({
  open,
  onClose,
  onSubmit,
  initialValues,
  isEdit
}: Props) {
  const [costCenterId, setCostCenterId] = useState("");
  const [profession, setProfession] = useState("");
  const [minRequiredTG, setMinRequiredTG] = useState("");
  const [professionGroups, setProfessionGroups] = useState<
    { country: string; professions: string[] }[]
  >([]);
  const { costCenters } = useCostCenters();
  const costCenterOptions = getOptions(costCenters);

  useEffect(() => {
    if (open) {
      setCostCenterId(initialValues?.costCenterId || "");
      setProfession(initialValues?.profession || "");
      setMinRequiredTG(initialValues?.minRequiredTG?.toString() || "");
    }
  }, [open]);

  useEffect(() => {
    ProfessionsByCountry.func({}).then((result) => {
      setProfessionGroups(result.data.professionsByCountry);
    });
  }, []);

  const handleSubmit = () => {
    const threshold = parseFloat(minRequiredTG);
    if (isNaN(threshold)) return;

    onSubmit({
      costCenterId,
      profession: profession || null,
      minRequiredTG: threshold
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Redigera TG-krav" : "Lägg till TG-krav"}
      </DialogTitle>
      <DialogContent>
        <TextField
          select
          label={locale.configEditor.costCenter}
          value={costCenterId}
          onChange={(e) => setCostCenterId(e.target.value)}
          fullWidth
          margin="normal"
          disabled={isEdit}
        >
          {costCenterOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Profession"
          value={profession}
          onChange={(e) => setProfession(e.target.value)}
          fullWidth
          margin="normal"
          disabled={isEdit}
        >
          <MenuItem value="">Standard (alla)</MenuItem>
          {professionGroups.flatMap((group) => [
            <ListSubheader key={group.country}>{group.country}</ListSubheader>,
            ...[...group.professions]
              .sort((a, b) =>
                getProfessionDisplayName(a).localeCompare(getProfessionDisplayName(b), "sv")
              )
              .map((p) => (
              <MenuItem key={p} value={p}>
                {getProfessionDisplayName(p)}
              </MenuItem>
            ))
          ])}
        </TextField>
        <TextField
          label="Min TG (%)"
          type="number"
          value={minRequiredTG}
          onChange={(e) => setMinRequiredTG(e.target.value)}
          fullWidth
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!costCenterId || !minRequiredTG}
        >
          {isEdit ? "Spara" : "Lägg till"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
